import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { intlLocale, toLanguage, type Language } from '../i18n.js';
import { AlertIcon, CheckIcon, ClockIcon, ExternalIcon, GearIcon, XIcon } from './icons.js';
import { BackButton } from './BackButton.js';
import { SiteRail } from './SiteRail.js';
import { useMediaQuery } from './useMediaQuery.js';
import { StatusBadge } from './ui.js';
import { useSites } from '../pages/Overview.js';

export type Grade = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
export type TileStatus = 'good' | 'warn' | 'bad' | 'unknown' | 'setup';

/** Current UI language as the API expects it. */
export function useLocale(): Language {
  const { i18n } = useTranslation();
  return toLanguage(i18n.language);
}

/** Where the full details of an area live (the area page links there). */
export const AREA_DETAILS = {
  availability: '/incidents',
  security: '/findings?category=security',
  links: '/links',
  seo: '/findings?category=seo',
  performance: '/speed',
  email: '/findings?category=email',
  compliance: '/findings?category=compliance',
} as const;

/** Site pages (PRD §5: two navigation levels at most). */
const SECTIONS = [
  ['', 'overview'],
  ['/changes', 'changes'],
  ['/findings', 'findings'],
  ['/incidents', 'incidents'],
  ['/links', 'links'],
  ['/speed', 'speed'],
  ['/ai-search', 'ai'],
  ['/modernization', 'modernization'],
  ['/settings', 'settings'],
] as const;

/** Name, grade and state of the site above the menu (T-82); from the cached site list. */
/** The site's address as a link that opens the site itself in a new tab. */
export function SiteAddress({
  url,
  className = '',
  children,
}: {
  url: string;
  className?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-1 break-all text-blue-700 underline-offset-2 hover:text-blue-900 hover:underline ${className}`}
    >
      {children}
      <ExternalIcon />
      <span className="sr-only">{` (${t('siteNav.opensNewTab')})`}</span>
    </a>
  );
}

function SiteIdentity({ siteId }: { siteId: string }) {
  const sites = useSites();
  const site = sites.data?.items.find((s) => s.id === siteId);
  if (!site) return null;
  const grade = site.healthGrade as Grade | null;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 lg:flex-col lg:items-start">
      <div className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden="true"
          className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-xl font-extrabold ${grade ? GRADE_STYLE[grade] : 'bg-slate-200 text-slate-800'}`}
        >
          {grade ?? '–'}
        </span>
        <div className="min-w-0">
          <p className="font-bold break-words">{site.name}</p>
          <SiteAddress url={site.url} className="text-sm">
            {site.host}
          </SiteAddress>
        </div>
      </div>
      <span className="ml-auto lg:ml-0">
        <StatusBadge state={site.status === 'paused' ? 'paused' : site.uptimeState} />
      </span>
    </div>
  );
}

/** Open problems as a badge in the menu: red when some are urgent. */
function useProblemBadge(siteId: string) {
  const sites = useSites();
  const site = sites.data?.items.find((s) => s.id === siteId);
  if (!site) return null;
  const f = site.openFindings;
  const total = f.critical + f.high + f.medium + f.low + f.info;
  if (total === 0) return null;
  return { total, urgent: f.critical + f.high > 0 };
}

/**
 * Site pages with a side menu (layout A, T-82): on wide screens the menu stands left of the page,
 * on narrow ones above it. PRD §5: two navigation levels at most.
 */
export function SiteLayout() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const problems = useProblemBadge(siteId);
  // The same width as the `wide` breakpoint: one panel in the page, never a hidden second copy.
  const wide = useMediaQuery('(min-width: 87.5rem)');
  const link = ({ isActive }: { isActive: boolean }) =>
    `flex min-h-11 w-full items-center justify-between gap-2 rounded-lg px-3 font-semibold ${isActive ? 'bg-blue-50 text-blue-900 ring-1 ring-blue-200' : 'text-slate-800 hover:bg-slate-100'}`;
  return (
    <div className="lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:items-start lg:gap-8 wide:grid-cols-[15rem_minmax(0,1fr)_21rem]">
      <aside className="mb-6 flex flex-col gap-3 lg:sticky lg:top-4 lg:mb-0 print:hidden">
        <p>
          <BackButton to="/">{t('site.back')}</BackButton>
        </p>
        <SiteIdentity siteId={siteId} />
        <nav
          aria-label={t('siteNav.label')}
          className="rounded-xl border border-slate-200 bg-white p-2"
        >
          <ul className="grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-1">
            {SECTIONS.map(([path, key]) => (
              <li
                key={key}
                className={
                  key === 'settings' ? 'lg:mt-1 lg:border-t lg:border-slate-100 lg:pt-1' : undefined
                }
              >
                <NavLink to={`/sites/${siteId}${path}`} end={path === ''} className={link}>
                  <span>{t(`siteNav.${key}`)}</span>
                  {key === 'findings' && problems ? (
                    <span
                      className={`rounded-full px-2 text-sm font-bold tabular-nums ${problems.urgent ? 'bg-red-100 text-red-900' : 'bg-slate-100 text-slate-800'}`}
                    >
                      <span className="sr-only">{t('siteNav.openProblems')}: </span>
                      {problems.total}
                    </span>
                  ) : null}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
      <div className="min-w-0">
        {wide ? null : <SiteRail siteId={siteId} variant="top" />}
        <Outlet />
      </div>
      {wide ? (
        <div className="sticky top-4">
          <SiteRail siteId={siteId} variant="side" />
        </div>
      ) : null}
    </div>
  );
}

export const GRADE_STYLE: Record<Grade, string> = {
  A: 'bg-green-700 text-white',
  B: 'bg-green-600 text-white',
  C: 'bg-amber-300 text-amber-950',
  D: 'bg-orange-400 text-orange-950',
  E: 'bg-red-600 text-white',
  F: 'bg-red-800 text-white',
};

/** Grade letter with the score; the letter is also read out (never colour alone). */
export function GradeBadge({ grade, score }: { grade: Grade; score: number }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-3">
      <span
        aria-hidden="true"
        className={`inline-flex h-16 w-16 items-center justify-center rounded-lg text-4xl font-bold ${GRADE_STYLE[grade]}`}
      >
        {grade}
      </span>
      <span className="sr-only">{t('health.gradeSr', { grade })}</span>
      <span className="text-2xl font-bold">
        {score}
        <span className="text-base font-normal text-slate-700"> / 100</span>
      </span>
    </span>
  );
}

/** Score ranges of the grades (monitoring-rules §6, HEALTH.grades in @website-guard/rules). */
const GRADE_RANGES: readonly [Grade, string][] = [
  ['A', '90–100'],
  ['B', '80–89'],
  ['C', '65–79'],
  ['D', '50–64'],
  ['E', '35–49'],
  ['F', '0–34'],
];

/** All grades as a small scale under the badge, the site's own one marked (owner's idea, 25.09). */
export function GradeScale({ grade }: { grade: Grade }) {
  const { t } = useTranslation();
  return (
    <ol aria-label={t('health.scaleLabel')} className="flex flex-wrap gap-1.5">
      {GRADE_RANGES.map(([g, range]) => {
        const current = g === grade;
        return (
          <li
            key={g}
            title={`${g}: ${t(`health.scale.${g}`)} (${range})`}
            aria-current={current ? 'true' : undefined}
            className="flex w-12 flex-col items-center gap-0.5"
          >
            <span
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-bold ${GRADE_STYLE[g]} ${current ? 'ring-2 ring-slate-900 ring-offset-2' : 'opacity-50'}`}
            >
              {g}
            </span>
            <span
              className={`text-xs tabular-nums ${current ? 'font-semibold text-slate-900' : 'text-slate-600'}`}
            >
              {range}
            </span>
            <span className="sr-only">
              {t(`health.scale.${g}`)}
              {current ? ` – ${t('health.scaleYou')}` : ''}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function TileStatusIcon({ status }: { status: TileStatus }) {
  const { t } = useTranslation();
  const look = {
    good: ['text-green-800', <CheckIcon key="i" />],
    warn: ['text-amber-800', <AlertIcon key="i" />],
    bad: ['text-red-800', <XIcon key="i" />],
    unknown: ['text-slate-600', <ClockIcon key="i" />],
    setup: ['text-violet-800', <GearIcon key="i" />],
  }[status] as [string, ReactNode];
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-semibold ${look[0]}`}>
      {look[1]}
      {t(`tileStatus.${status}`)}
    </span>
  );
}

export function formatPrice(cents: number, language: string) {
  return new Intl.NumberFormat(intlLocale(language), {
    style: 'currency',
    currency: 'EUR',
    currencyDisplay: 'narrowSymbol', // uk-UA would print "49 EUR"; de/en already show €
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** Invoice amounts: always with cents. */
export function formatMoney(cents: number, language: string) {
  return new Intl.NumberFormat(intlLocale(language), {
    style: 'currency',
    currency: 'EUR',
    currencyDisplay: 'narrowSymbol',
  }).format(cents / 100);
}

/** 32 hex characters; getRandomValues also works outside secure contexts, unlike randomUUID. */
export const randomKey = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');

type ScanType = 'full' | 'crawl' | 'seo_home' | 'performance';

/** Starts a scan by hand with a fresh Idempotency-Key per click. */
export function useStartScan(siteId: string, type: ScanType) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites/{siteId}/scans', {
          params: {
            path: { siteId },
            header: { 'idempotency-key': randomKey() },
          },
          body: { type },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', siteId] }),
  });
}

/** A scan the queue closed without scanning: the site was paused or deleted (SP-A2.42). */
export const notScanned = (scan: { status: string; errorCode?: string | null }) =>
  scan.status === 'failed' && (scan.errorCode === 'SITE_PAUSED' || scan.errorCode === 'SITE_GONE');

/** Explains why a scan could not be started. */
export function scanError(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.code === 'FORBIDDEN') return t('scan.unverified');
    if (error.code === 'RATE_LIMITED') return t('scan.limit');
    // A paused site is not scanned (SP-A2.42).
    if (error.code === 'CONFLICT') return t('scan.paused');
  }
  return errorMessage(error, t as never);
}
